import { useEffect, useRef, useState } from "react";
import { Check, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { QuizStudent, QuestionHead, Totals } from "@/modes/quiz/QuizStudent";
import { QuizTeacher } from "@/modes/quiz/QuizTeacher";
import { QuizSettings } from "@/modes/quiz/QuizSettings";
import { Final, Head, TeamScores } from "@/modes/quiz/QuizBoard";

const colorOf = (teams, id) => teams.find((t) => t.id === id)?.color || "#94A3B8";

export const Feed = ({ items, teams, limit, big, testId }) => (
  <div className={`flex flex-col justify-end gap-[0.8vh] overflow-hidden ${big ? "h-full" : "max-h-72"}`} data-testid={testId}>
    {(items || []).slice(-limit).map((m, i) => (
      <div key={`${i}-${m.nick}`} className={`rounded-xl px-3 py-1.5 ${m.correct ? "bg-[#34D399]/15 border border-[#34D399]/60" : "bg-white/5"} ${big ? "text-[min(1.6rem,2.6vh)]" : "text-sm"}`}>
        <b style={{ color: colorOf(teams, m.team_id) }}>{m.nick}</b>{" "}
        {m.correct ? <span className="text-[#6EE7B7] font-bold">bildi! ({m.rank + 1}.)</span> : <span className="text-slate-200">{m.text}</span>}
      </div>
    ))}
  </div>
);

const GuessInput = ({ send, disabled }) => {
  const [text, setText] = useState("");
  const submit = (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    send({ type: "answer", text });
    setText("");
  };
  return (
    <form onSubmit={submit} className="flex gap-2" data-testid="guess-form">
      <Input value={text} onChange={(e) => setText(e.target.value)} maxLength={40} disabled={disabled} placeholder="Tahminini yaz…"
        className="h-14 text-lg" data-testid="guess-input" autoComplete="off" />
      <Button type="submit" disabled={disabled || !text.trim()} className="h-14 px-5" data-testid="guess-send" aria-label="Gönder"><Send className="h-5 w-5" /></Button>
    </form>
  );
};

const Guessing = ({ state, send, remaining }) => {
  const g = state.my_guess || {};
  return (
    <div className="space-y-5" data-testid="student-guess">
      <QuestionHead state={state} q={state.question} remaining={remaining} />
      {g.correct ? (
        <div className="rounded-xl border-2 border-[#34D399] bg-[#34D399]/10 p-4 flex items-center gap-3" data-testid="guess-correct">
          <Check className="h-7 w-7 text-[#34D399]" />
          <p className="font-display text-2xl font-bold">Bildin! {g.rank}. sıradasın</p>
        </div>
      ) : <GuessInput send={send} disabled={remaining <= 0} />}
      <p className="text-sm text-slate-400" data-testid="guess-count">{g.guesses || 0} tahmin · yanlış tahminin cezası yok</p>
      <Feed items={state.feed} teams={state.teams} limit={12} testId="student-feed" />
    </div>
  );
};

const GuessResult = ({ state }) => {
  const r = state.results;
  if (r.skipped) return <p className="font-display text-2xl font-bold" data-testid="student-skipped">Bu soru atlandı.</p>;
  const ok = r.mine?.status === "correct";
  return (
    <div className="space-y-5" data-testid="student-guess-result">
      <QuestionHead state={state} q={state.question} />
      <div className={`rounded-xl p-5 border-2 ${ok ? "border-[#34D399] bg-[#34D399]/10" : "border-slate-600 bg-white/5"}`}>
        <p className="font-display text-3xl font-black" data-testid="student-result-status">{ok ? `Bildin! (${state.my_guess?.rank}.)` : "Bu sefer bilemedin"}</p>
        {ok && <p className="text-2xl font-bold text-[#6EE7B7] mt-2" data-testid="student-result-score">+{r.mine.score} puan</p>}
        <p className="mt-3 text-lg">Cevap: <b className="text-[#FACC15]" data-testid="student-guess-answer">{r.accepted?.join(" / ")}</b></p>
      </div>
      {r.explanation && <p className="text-slate-300">{r.explanation}</p>}
      <Totals state={state} />
    </div>
  );
};

export const GuessStudent = ({ state, send, remaining }) => {
  if (state.phase === "question") return <Guessing state={state} send={send} remaining={remaining} />;
  if (state.phase === "results") return <GuessResult state={state} />;
  return <QuizStudent state={state} send={send} remaining={remaining} />;
};

const Reveal = ({ state }) => {
  const r = state.results;
  if (r.skipped) return <p className="font-display text-[6vh] font-bold text-slate-300" data-testid="board-skipped">Soru atlandı</p>;
  return (
    <div className="space-y-[2vh]" data-testid="board-guess-reveal">
      <p className="eyebrow text-[2.2vh]">Cevap</p>
      <p className="font-display text-[min(5rem,9vh)] font-bold text-[#FACC15] leading-none" data-testid="board-guess-answer">{r.accepted?.[0]}</p>
      <p className="text-[min(1.75rem,2.8vh)] font-semibold" data-testid="board-correct-count">{r.correct}/{r.participants} bildi</p>
      <ol className="space-y-[0.8vh]">
        {(r.solvers || []).slice(0, 5).map((s) => (
          <li key={s.rank} className="text-[min(1.75rem,2.8vh)]"><b>{s.rank}.</b> <span style={{ color: colorOf(state.teams, s.team_id) }}>{s.nick}</span> · {s.score} puan</li>
        ))}
      </ol>
    </div>
  );
};

export const GuessBoard = ({ state, remaining }) => {
  if (state.phase === "finished") return <Final state={state} />;
  return (
    <div className="h-full flex flex-col gap-[2vh]">
      <Head state={state} remaining={remaining} />
      <div className="flex-1 min-h-0 grid grid-cols-[1fr_34vw] gap-[2vw]">
        <div className="min-h-0">
          {state.phase === "results" ? <Reveal state={state} /> : (
            <div className="space-y-[1vh]" data-testid="board-guess-solved">
              <p className="font-display text-[min(6rem,11vh)] font-bold leading-none text-[#34D399]">{state.solved || 0}<span className="text-slate-500 text-[5vh]"> / {state.expected}</span></p>
              <p className="text-[3vh] text-slate-300">kişi bildi · cevap gizli</p>
            </div>
          )}
        </div>
        <div className="panel p-[1.5vh] min-h-0"><Feed items={state.feed} teams={state.teams} limit={14} big testId="board-feed" /></div>
      </div>
      <TeamScores state={state} />
    </div>
  );
};

const nameOf = (state, sid) => {
  const s = state.students.find((x) => x.id === sid);
  return s ? `${s.first_name} ${s.last_name}` : "(ayrıldı)";
};

const TeacherFeed = ({ state }) => {
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ block: "nearest" }); }, [state.feed?.length]);
  return (
    <div className="panel p-5 space-y-3" data-testid="teacher-guess-feed">
      <h3 className="font-display text-xl font-bold">Tahmin akışı</h3>
      <p className="text-sm text-slate-400">Kabul edilen: <b className="text-[#6EE7B7]">{state.question?.data?.answers?.join(" / ")}</b></p>
      <div className="max-h-72 overflow-y-auto space-y-1 text-sm">
        {(state.feed || []).map((m, i) => (
          <p key={i}><b>{m.nick}</b> <span className="text-slate-500">({nameOf(state, m.sid)})</span>: {m.correct ? <span className="text-[#6EE7B7] font-bold">doğru ({m.rank + 1}.)</span> : m.text}</p>
        ))}
        <div ref={end} />
      </div>
    </div>
  );
};

export const GuessTeacher = ({ state, send }) => (
  <div className="space-y-6">
    {state.phase === "question" && <TeacherFeed state={state} />}
    <QuizTeacher state={state} send={send} />
  </div>
);

export const GuessSettings = ({ settings, onChange }) => <QuizSettings settings={settings} onChange={onChange} hideLive />;
